import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from '@phosphor-icons/react';
import { useModalStore } from '@/store/modal/store';
import { WalletService } from '@/core/services/walletService';
import { MasterAccountService } from '@/core/services/masterAccountService';
import { useMutation } from '@tanstack/react-query';
import Input from '@/core/components/dv-forms/input';
import { AllocationRepartition } from '@/types/main-account';
import { toast } from 'react-toastify';

const BuyDvxModal = () => {
  const { t, i18n } = useTranslation();
  const { setModal } = useModalStore();

  const [allocations, setAllocations] = useState<AllocationRepartition[]>([]);
  const [amount, setAmount] = useState<number | ''>('');
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<'input' | 'confirm'>('input');

  const { mutate: loadAllocations, isLoading: isLoadingAllocations } = useMutation({
    mutationFn: () => MasterAccountService.getMainAccountAllocation(),
    onSuccess: (data) => setAllocations(data || []),
  });

  const { mutate: buyDvx, isLoading: isBuying } = useMutation({
    mutationFn: (qty: number) => WalletService.buyDvx(qty),
    onSuccess: (res) => {
      if (res.status !== 'success') {
        return;
      }
      const dvxSent = res.dvxSent.toLocaleString(
        (i18n?.language || "en"),
        { minimumFractionDigits: 2, maximumFractionDigits: 2 }
      );

      toast.success(t('common.dvx.bought') + dvxSent + ' DVX');
      onClose();
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.message || t('message.something_went_wrong');
      toast.error(String(msg));
      onClose();
    },
  });

  useEffect(() => {
    loadAllocations();
  }, []);

  const masterBalance = useMemo(() => {
    const usdc = allocations.find((a) => a.asset?.toUpperCase() === 'USDC');
    // Prefer in_usdc if provided; fallback to value or amount
    const balance = usdc ? (typeof usdc.in_usdc === 'number' ? usdc.in_usdc : (typeof usdc.value === 'number' ? usdc.value : usdc.amount)) : 0;
    return balance ?? 0;
  }, [allocations]);

  const onClose = () => setModal({ buyDvx: false });

  const validate = (val: number): string | null => {
    if (Number.isNaN(val)) return t('form.required.amount') as string;
    if (val <= 0) return t('form.invalid.lower_than_zero') as string;
    if (val > masterBalance) return t('form.invalid.amount_exceeds_balance') as string;
    return null;
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value === '' ? '' : Number(e.target.value);
    setAmount(val);
    if (val === '') {
      setError(t('form.required.amount') as string);
    } else {
      setError(validate(val as number));
    }
  }

  const onConfirm = () => {
    if (amount === '') {
      setError(t('form.required.amount') as string);
      return;
    }
    const err = validate(amount as number);
    if (err) {
      setError(err);
      return;
    }
    setStep('confirm');
  }

  const onBuy = () => {
    if (amount === '' || error) return;
    buyDvx(amount as number);
  }

  return (
    <div className="general-modal" style={{ maxWidth: '528px' }}>
      <div className="general-modal__header">
        {t('common.dvx.buy')}
        <div className="general-modal__close" onClick={onClose}>
          <X />
        </div>
      </div>
      <div className="general-modal__content" style={{ overflow: 'unset' }}>
        <div className="dv-form">
          {step === 'input' ? (
            <>
              <div style={{ marginBottom: 12 }}>
                <div className="txt-body-m" style={{ opacity: 0.8 }}>
                  {t('dca.available_balance')}: <strong>{masterBalance} USDC</strong>
                </div>
              </div>
              <Input
                name="amount"
                label={t('common.amount')}
                placeholder=""
                register={() => ({}) as any}
                errors={error ? { amount: { message: error } } as any : undefined}
                startAdornment={`$`}
                type="number"
                value={amount}
                onChange={handleChange}
              />
            </>
          ) : (
            <div className="txt-body-m">
              {t('common.dvx.confirm_purchase')} {' '}
              <strong>
                {typeof amount === 'number'
                  ? amount.toLocaleString(i18n?.language || 'en', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                  : amount} {' '}
                USDC
              </strong>.
            </div>
          )}
        </div>
      </div>
      <div className="general-modal__footer">
        {step === 'confirm' ? (
          <>
            <button disabled={isBuying} className="cta-button cta-large cta-grey"
                    onClick={() => setStep('input')}>
              <span>{t('common.back') || 'Back'}</span>
            </button>
            <button disabled={!!error || isLoadingAllocations || isBuying || allocations.length === 0} className="cta-button cta-large cta-blue" onClick={onBuy}>
              <span>{t('common.confirm') || 'Confirm'}</span>
            </button>
          </>
        ) : (
          <button disabled={!!error || isLoadingAllocations || isBuying || allocations.length === 0} className="cta-button cta-large cta-blue" onClick={onConfirm}>
            <span>{t('common.continue')}</span>
          </button>
        )}
      </div>
    </div>
  );
}

export default BuyDvxModal;
